import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { displayPrice, htmlDecode } from '../helpers'
import { CourseDataType, CourseDataQuantityType, ToggleFeaturesType } from '../types'
import { QuantitySwitch } from '../components/base-ui'

interface CourseCardProps {
  course: CourseDataType
  quantityCourses: CourseDataQuantityType[]
  currencySymbol: string
  maximumCourseQuantity: number
  bookingFeatures: ToggleFeaturesType
  totalQuantity: number
  onUpdateQuantityCourse: (course: CourseDataQuantityType) => void
  disabled?: boolean
}

const CourseCard = ({ course, currencySymbol, quantityCourses, maximumCourseQuantity, bookingFeatures, totalQuantity, onUpdateQuantityCourse, disabled = false }: CourseCardProps) => {
  const { t } = useTranslation()
  const targetQuantityCourse = quantityCourses.find(quantityCouse => quantityCouse.id === course.id)

  let currentQuantity = 0
  if (course.course_type === 'room') {
    currentQuantity = targetQuantityCourse ? targetQuantityCourse?.number_of_party? targetQuantityCourse.number_of_party: 0: 0
  } else {
    currentQuantity = targetQuantityCourse ? targetQuantityCourse.quantity: 0
  }
  const _maximumCourseQuantity = course.capacity ?? maximumCourseQuantity
  const maxValue = bookingFeatures.client_booking_limit ? _maximumCourseQuantity - totalQuantity + currentQuantity: _maximumCourseQuantity
  const isDisabledIncreasing = bookingFeatures.client_booking_limit ? _maximumCourseQuantity === totalQuantity: _maximumCourseQuantity === currentQuantity

  return (
    <CourseCardStyled isDisabled={disabled}>
      <Card>
        <CourseName className="yz-h6-still">{htmlDecode(course.displayName)}{course.isLunchOnly ? (<><br />({t('lunch_only')})</>): ''}</CourseName>
        <CourseRow>
          <CoursePrice>{displayPrice(course.price)} {currencySymbol}**</CoursePrice>
          {
            course.course_type === 'room' ? (
              <div>**{t('max')}. {course.maximum_guest} {t('guests')}</div>
            ): 
            (
              <>
              {
                course.minimum_guest > 1 ? (
                  <div>**{(t('min'))}. {course.minimum_guest} {t('guests')}</div>
                ): (
                  <div>{t('number_of_guests')}</div>
                )
              }
              </>
            )
          }
        </CourseRow>
        <QuantitySwithcWrapper>
          {
            course.course_type === 'room' ? (
              <QuantitySwitch
                disabled={disabled} 
                isTypable={true} 
                quantity={currentQuantity} 
                minValue={course.minimum_guest} 
                maxValue={course.maximum_guest}
                disabledIncreasing={isDisabledIncreasing || disabled}
                onUpdate={(quantity) => {
                  const roomQuantity = quantity > 0? 1: 0
                  onUpdateQuantityCourse({ ...course , quantity: roomQuantity, number_of_party: quantity})
                }}
              />
            ): (
              <QuantitySwitch
                disabled={disabled || maxValue < course.minimum_guest} 
                isTypable={true} 
                quantity={currentQuantity} 
                minValue={course.minimum_guest} 
                maxValue={maxValue}
                disabledIncreasing={isDisabledIncreasing || disabled || maxValue < course.minimum_guest}
                onUpdate={(quantity) => {
                  onUpdateQuantityCourse({ ...course , quantity })
                }}
              />
            )
          }
        </QuantitySwithcWrapper>
      </Card>
    </CourseCardStyled>
  )
}

export default CourseCard

const CourseCardStyled = styled.div<{ isDisabled: boolean }>`
  padding: 0 12px;
  width: 50%;
  margin-bottom: 24px;
  transition: opacity .3s;

  ${({ isDisabled }) => isDisabled ? `
    opacity: .5;
  `: ''}

  @media (max-width: 767px) {
    width: 100%;
    margin-bottom: 8px;
  }
`

const Card = styled.div`
  width: 100%;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  color: black;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const CourseName = styled.h6`
  text-transform: uppercase;
  margin-bottom: 8px;

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 767px) {
    font-size: 16px;
  }
` 

const CourseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
  margin-top: auto;

  @media (max-width: 1024px) {
    font-size: 14px;
  }

  @media (max-width: 767px) {
    font-size: 12px;
  }
`

const CoursePrice = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: var(--global--color-background-secondary);

  @media (max-width: 767px) {
    font-size: 16px;
  }
`

const QuantitySwithcWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`